import React, { useEffect, useMemo, useState } from "react";
import Page from "@/components/page";
import AuthWebLayout from "@/components/auth/authWebLayout";
import AuthMobileLayout from "@/components/auth/authMobileLayout";
import { useTranslation } from "react-i18next";
import "./style.scss";
import { withPageInitialLoad } from "@/libs/pageDataBridge";

import { useAppDispatch, useAppSelector } from "@/redux/store";
import { useHistory, useLocation } from "react-router-dom";
import { useRouteSlug, useLanguage, useCountryShort } from "@/hooks";
import {
  selectGetAuthData,
  selectGetResponseUpdateDetails
} from "@/util/selectors";

import { TabKey, SignInMode } from "./types";
import { MobileConfirm } from "./components/mobileConfirm";
import { MobileOtp } from "./components/mobileOtp";
import { StandardMode } from "./components/standardMode";

import {
  verifyAndAuthenticateOtpUser,
  authenticateUserByEmail,
  resetAPIError
} from "@/redux/actions/homepage.action";
import {
  resetAuthData,
  resetTokenGeneratorCheckoutGuest,
  saveResponseUpdateDetailsData,
  sendLoginOtp
} from "@/redux/actions/authConfig.action";
import { getAuthEmail, clearAuthEmail } from "@/util/auth";
import useIsSmallScreen from "@/hooks/useIsSmallScreen";
import UpdateDetails from "@/components/auth/updateDetailsSocial";
import OtpVerification from "./components/otpVerification";
import { DRAFT_STORAGE_KEY, SIGNUP_API_CODE_NAMES } from "@/constants/signUp";
import { IS_AUTH_FROM_CHECKOUT, CHECKOUT_ID } from "@/constants";
import { changeCheckoutData } from "@/redux/actions/checkout.action";
import { setCountryListData } from "@/redux/actions/common.action";
import { DynamicYieldEventService } from "@/services";
import { clearFromSessionStorage } from "@/util/storage";
import { normalizePathFromQuery } from "@/utils";

const SignInPage: React.FC = () => {
  const { t } = useTranslation(["login", "seo"]);
  const history = useHistory();
  const routeSlug = useRouteSlug();
  const language = useLanguage();
  const countrySlug = useCountryShort();
  const dispatch = useAppDispatch();
  const { isSmallScreen } = useIsSmallScreen();
  const location = useLocation();

  const authData = useAppSelector(selectGetAuthData);
  const responseUpdateDetails = useAppSelector(selectGetResponseUpdateDetails);

  const [mobileInitialExpiresAtMs, setMobileInitialExpiresAtMs] = useState<
    number | null
  >(null);
  const [mobileInitialSendCount, setMobileInitialSendCount] =
    useState<number>(0);

  const options = useMemo<TabKey[]>(() => {
    const raw = (authData?.signInOptions ?? []) as string[];
    const normalized = raw.filter(
      (o): o is TabKey => o === "password" || o === "emailOtp"
    );
    return normalized.length ? normalized : (["password"] as TabKey[]);
  }, [authData?.signInOptions]);

  const hasPassword = options.includes("password");
  const hasEmailOtp = options.includes("emailOtp");
  const defaultTab: TabKey = hasPassword ? "password" : "emailOtp";

  const [mode, setMode] = useState<SignInMode>(SignInMode.Standard);
  const [activeTab, setActiveTab] = useState<TabKey>(defaultTab);
  const [otpToken, setOtpToken] = useState(null);
  const [otpMobileConfirmToken, setOtpMobileConfirmToken] = useState(null);
  const [mobilePhoneOtp, setMobilePhoneOtp] = useState(null);
  const [mobileConfirmSendError, setMobileConfirmSendError] = useState(null);
  const [phoneToDisplay, setPhoneToDisplay] = useState(null);

  const authToken = authData?.token;

  useEffect(() => {
    setMobileConfirmSendError("");
  }, [mode]);

  useEffect(() => {
    const run = async () => {
      if (responseUpdateDetails) {
        setMode(SignInMode.UpdateDetails);
        return;
      }
      const isSocialModes =
        mode === SignInMode.UpdateDetails ||
        mode === SignInMode.OtpVerification;

      const validatedUserEmail = getAuthEmail();
      //should be fixed
      if (!validatedUserEmail && !authData.email && !isSocialModes) {
        const { search } = location;
        history.replace(`/${routeSlug}/authorization/${search || ""}`);
        return;
      }
      try {
        if (
          !isSocialModes &&
          !authData?.email &&
          authData.email !== validatedUserEmail
        ) {
          await dispatch(
            authenticateUserByEmail({ email: validatedUserEmail })
          );
        }
      } catch (e) {
        console.log("error", e);
      }
    };

    run();
  }, [responseUpdateDetails]);

  useEffect(() => {
    return () => {
      clearAuthEmail();
      clearFromSessionStorage(DRAFT_STORAGE_KEY);
    };
  }, []);

  useEffect(() => setActiveTab(defaultTab), [defaultTab]);

  const cartRedirectionUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const checkoutRedirect = searchParams.get("redirect");
    return checkoutRedirect;
  }, [location.search]);

  const pdpRedirectionUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("redirect");
  }, [location.search]);

  const myAccountRedirectionUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("redirect");
  }, [location.search]);

  const redirection = useMemo(() => {
    const redirectionTail = location.search.split("?")[1];
    return `/${routeSlug}/${
      pdpRedirectionUrl ||
      cartRedirectionUrl ||
      myAccountRedirectionUrl ||
      redirectionTail ||
      ""
    }`;
  }, [
    pdpRedirectionUrl,
    cartRedirectionUrl,
    myAccountRedirectionUrl,
    routeSlug
  ]);

  const redirectParam = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const raw = sp.get("redirect") || "";
    return normalizePathFromQuery(raw);
  }, [location.search]);

  const isCartRedirect = useMemo(() => {
    const p = redirectParam.replace(/\/+$/, "");
    return p === "my-cart";
  }, [redirectParam]);

  const onEditEmail = () => history.push(`/${routeSlug}/authorization/`);

  const handleMobileConfirm = async () => {
    const res: any = await dispatch(
      sendLoginOtp({ token: authToken, channel: "sms" })
    );
    if (!res.success) {
      setMobileConfirmSendError(res.message || "Send OTP failed");
      return;
    }
    const retry = res?.retryAfterSeconds ?? 0;
    setOtpMobileConfirmToken(res.emailOtpToken);
    setMobileInitialExpiresAtMs(Date.now() + retry * 1000);
    setMobileInitialSendCount(1);
    setMode(SignInMode.MobileOtp);
  };

  const requestMobileOtp = async () => {
    const res: any = await dispatch(
      sendLoginOtp({ token: authToken, channel: "sms" })
    );
    if (!res.success) {
      return {
        success: false,
        message: res.message
      };
    }
    setOtpMobileConfirmToken(res.emailOtpToken);
    return { retry: res?.retryAfterSeconds, success: true };
  };

  const verifyMobileOtp = async (code: string) => {
    const resp: any = await dispatch(
      verifyAndAuthenticateOtpUser({
        code,
        token: otpMobileConfirmToken,
        history,
        language,
        country: countrySlug,
        redirect: false,
        onAuth: onAuthCallback,
        withCartRedirect: Boolean(isCartRedirect),
        onAuthMissingFieldsError
      })
    );
    return { success: !!resp?.success, message: resp?.message };
  };

  const onVerifySuccessRedirect = () => {
    history.replace(redirection);
  };

  const handleOtpCallback = (
    token: string,
    phone: string,
    structuredPhone: string
  ) => {
    setOtpToken(token), setMode(SignInMode.OtpVerification);
    setMobilePhoneOtp(phone);
    setPhoneToDisplay(structuredPhone);
  };

  const onMissingFieldsBackAction = () => {
    clearFromSessionStorage(DRAFT_STORAGE_KEY);
    authData?.email
      ? setMode(SignInMode.Standard)
      : history.push(`/${routeSlug}/authorization/`);
  };

  const onAuthMissingFieldsError = (response, registrationRequired) => {
    dispatch(saveResponseUpdateDetailsData(response));
    if (response.code === SIGNUP_API_CODE_NAMES.MISSING_FIELDS_REQUIRED) {
      history.push({
        pathname: `/${routeSlug}/login/`,
        search: location.search || ""
      });
    }
  };

  const onAuthCallback = response => {
    if (response?.data?.userProfile?.customerId) {
      dispatch(resetTokenGeneratorCheckoutGuest());

      dispatch(changeCheckoutData("continueAsGuest", false));
      dispatch(changeCheckoutData("step1Completed", true));
      DynamicYieldEventService.login(response?.data?.userProfile?.email);
      if (localStorage.getItem(IS_AUTH_FROM_CHECKOUT)) {
        dispatch(setCountryListData([]));
        history.push(
          `/${routeSlug}/checkout/${localStorage.getItem(CHECKOUT_ID)}/`
        );
        localStorage.removeItem(IS_AUTH_FROM_CHECKOUT);
        return;
      }
      dispatch(resetAPIError());
    }

    if (response?.data?.userProfile?.customerId && redirection) {
      history.replace(redirection);
    }
  };

  const content = (
    <Page
      title={t("seo:signInTitle")}
      description={t("seo:defaultPageDescription")}
      className="login_user_page_wrapper"
    >
      <section className="signin_box">
        {mode === SignInMode.UpdateDetails ? (
          <UpdateDetails
            isMobile={isSmallScreen}
            handleOtpCallback={handleOtpCallback}
            onBack={onMissingFieldsBackAction}
            signIn
          />
        ) : (
          <>
            {mode === SignInMode.Standard && (
              <StandardMode
                email={authData?.email}
                canPassword={hasPassword}
                canEmailOtp={hasEmailOtp}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                onEditEmail={onEditEmail}
                onSwitchToMobile={() => setMode(SignInMode.MobileConfirm)}
                showTabs={options.length > 1}
                onAuthMissingFieldsError={onAuthMissingFieldsError}
                onAuthCallback={onAuthCallback}
                withCartRedirect={isCartRedirect}
              />
            )}

            {mode === SignInMode.MobileConfirm && (
              <MobileConfirm
                maskedNumber={authData?.emailOtpMaskedPhone}
                onBack={() => setMode(SignInMode.Standard)}
                onConfirm={handleMobileConfirm}
                mobileConfirmSendError={mobileConfirmSendError}
              />
            )}

            {mode === SignInMode.MobileOtp && (
              <MobileOtp
                maskedNumber={authData?.emailOtpMaskedPhone}
                emailFallback={authData?.email}
                onBack={() => setMode(SignInMode.MobileConfirm)}
                verifyEmailOtp={verifyMobileOtp}
                requestMobileOtp={requestMobileOtp}
                initialExpiresAtMs={mobileInitialExpiresAtMs}
                initialSendCount={mobileInitialSendCount}
                onSuccess={onVerifySuccessRedirect}
              />
            )}
            {mode === SignInMode.OtpVerification && (
              <OtpVerification
                otpToken={otpToken}
                verificationPhone={phoneToDisplay}
                handleEditClick={() => setMode(SignInMode.UpdateDetails)}
                redirection={redirection}
              />
            )}
          </>
        )}
      </section>
    </Page>
  );

  return isSmallScreen ? (
    <AuthMobileLayout>{content}</AuthMobileLayout>
  ) : (
    <AuthWebLayout>{content}</AuthWebLayout>
  );
};

export default withPageInitialLoad({
  pageName: "SignInPage",
  page: SignInPage,
  ssr: false,
  loader: true
});
